import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Container,
  Alert,
  Spinner,
  Card,
  ListGroup,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { useEditMode } from "../../Context/EditModeContext";
import "./QuizLesson.css";
import { FaTrashCan } from "react-icons/fa6";
import QuizAttempt from "../QuizAttempt/QuizAttempt";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const QuizLesson = ({ lesson, setLesson }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [newChoices, setNewChoices] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [header, setHeader] = useState(t("quiz_default_header"));
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [previousHeader, setPreviousHeader] = useState("");
  const { authData } = useAuth();
  const { isEditMode, toggleEditMode } = useEditMode();
  const { id } = useParams();

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/quizzes/${lesson.quiz}/questions`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || t("fetch_error"));
        }

        setQuestions(data.questions);
        setHeader(data.header || t("undefined_header"));
      } catch (err) {
        setError(err.message);
        console.error(err);
      }
    };

    fetchQuestions();
  }, [lesson.quiz, t]);

  const handleAddChoice = () => {
    setNewChoices([...newChoices, { text: "", isCorrect: false }]);
  };

  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...newChoices];
    updatedChoices[index].text = value;
    setNewChoices(updatedChoices);
  };

  const handleToggleCorrect = (index) => {
    const updatedChoices = [...newChoices];
    updatedChoices[index].isCorrect = !updatedChoices[index].isCorrect;
    setNewChoices(updatedChoices);
  };

  const handleAddQuestion = async () => {
    setError("");
    setSuccessMessage("");
    if (!newQuestion.trim() || newChoices.length === 0) {
      setError(t("fill_question_and_choices"));
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quizzes/${lesson.quiz}/questions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: newQuestion,
            answers: newChoices.map((choice) => choice.text),
            correct_answers: newChoices
              .filter((choice) => choice.isCorrect)
              .map((choice) => choice.text),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("add_question_error"));
      }

      setQuestions([...questions, data.question]);
      setNewQuestion("");
      setNewChoices([]);
      setSuccessMessage(t("question_added_success"));
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quizzes/${lesson.quiz}/questions/${questionId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || t("delete_question_error"));
      }

      setQuestions(questions.filter((q) => q.id !== questionId));
      setSuccessMessage(t("question_deleted_success"));
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  const handleHeaderUpdate = async (newHeader) => {
    setPreviousHeader(header);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quizzes/${lesson.quiz}/header`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ header: newHeader }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("update_header_error"));
      }

      setHeader(data.header);
      setSuccessMessage(t("header_updated_success"));
    } catch (err) {
      setError(err.message);
      setHeader(previousHeader);
    }
  };

  if (!isEditMode) {
    return <QuizAttempt lesson={lesson} toggleEditMode={toggleEditMode} />;
  }

  return (
    <Container className="p-5">
      <Row className="mb-4">
        <Col>
          {isEditingHeader ? (
            <Form.Control
              type="text"
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              onBlur={() => {
                setIsEditingHeader(false);
                handleHeaderUpdate(header);
              }}
              autoFocus
            />
          ) : (
            <div className="text-center">
              <h2
                className="quiz-header"
                onDoubleClick={() => setIsEditingHeader(true)}
              >
                {header}
              </h2>
              <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                {t("double_click_to_edit")}
              </p>
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={8} className="border-end pe-4">
          <h2 className="mb-4">{t("add_question")}</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>{t("question")}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("enter_question")}
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                />
              </Form.Group>

              <Form.Label>{t("answer_choices")}</Form.Label>
              <ListGroup className="mb-3">
                {newChoices.map((choice, index) => (
                  <ListGroup.Item
                    key={index}
                    className="d-flex align-items-center"
                  >
                    <Form.Control
                      type="text"
                      placeholder={`${t("choice")} ${index + 1}`}
                      value={choice.text}
                      onChange={(e) =>
                        handleChoiceChange(index, e.target.value)
                      }
                      className="me-3"
                    />
                    <Form.Check
                      type="checkbox"
                      label={t("correct")}
                      checked={choice.isCorrect}
                      onChange={() => handleToggleCorrect(index)}
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <Button variant="secondary" onClick={handleAddChoice}>
                {t("add_choice")}
              </Button>
            </Card.Body>
          </Card>
          <Button
            variant="primary"
            onClick={handleAddQuestion}
            disabled={isSaving}
            className="mb-4"
          >
            {isSaving ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              t("add_question")
            )}
          </Button>
        </Col>

        <Col md={4} className="ps-4">
          <h3 className="mb-3">{t("existing_questions")}</h3>
          {questions.length === 0 ? (
            <p className="text-muted">{t("no_questions_added")}</p>
          ) : (
            <ListGroup>
              {questions.map((q, index) => (
                <ListGroup.Item
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{q.question}</strong>
                    <ul>
                      {q.answers.map((answer, i) => (
                        <li
                          key={i}
                          style={{
                            color:
                              Array.isArray(q.correct_answer) &&
                              q.correct_answer.includes(answer)
                                ? "green"
                                : "inherit",
                          }}
                        >
                          {answer}{" "}
                          {Array.isArray(q.correct_answer) &&
                            q.correct_answer.includes(answer) &&
                            `(${t("correct")})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-delete-${index}`}>
                        {t("delete_question")}
                      </Tooltip>
                    }
                  >
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteQuestion(q.id)}
                    >
                      <FaTrashCan />
                    </Button>
                  </OverlayTrigger>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default QuizLesson;
